/**
 * Where the nodes of an expand are put: the ones that arrive beside a node already on the
 * canvas (`useCytoscapeInstance.updateData`).
 *
 * Neighbours are fanned on a ring round that node. A recording's transcript passages are not:
 * a recording holds dozens, each a timecode chip some 97 x 22 px, and on a ring of 180 px sixty
 * of them lie on top of each other. They are a timeline, so they are set as one: a block of
 * columns beside the recording, read top to bottom and then left to right, as tall and as wide
 * as what it holds. Every passage has a place of its own in the block — its number in the
 * recording — so a passage that is removed and expanded again comes back into the gap it left.
 * The block stands outside the ring, on the side the recording's other neighbours leave free.
 *
 * The geometry is pure; the two functions at the end read it off cytoscape nodes and write it
 * back.
 */

import type { NodeSingular } from 'cytoscape'
import { PASSAGE_LABEL } from './types'

export interface Point {
  x: number
  y: number
}

/** The radius of the ring neighbours are fanned on. */
export const FAN_RADIUS = 180

/** Free space between two chips of a column, and between two columns. */
const ROW_GAP = 6
const COLUMN_GAP = 12

/** A column is this tall before a second one is started: about the height of the ring. */
const COLUMN_ROWS = 16

/**
 * From the recording to the near edge of its timeline: past the ring, the nodes that sit on it
 * and the labels under them.
 */
const TIMELINE_CLEARANCE = FAN_RADIUS + 60

/** What the placement reads of a node that is to be placed. */
export interface PlacedChild {
  id: string
  /** The canvas's type of the node (`typeLabel`). */
  typeLabel: string
  width: number
  height: number
  /** A passage's number in its recording, from 0 (`index`); null for anything else. */
  index: number | null
  /** Where a passage starts in its recording (`start_ms`); null for anything else. */
  startMs: number | null
}

export type TimelineSide = 'left' | 'right'

/** What already stands round the node the children arrive beside. */
export interface Surroundings {
  /** Its passages that are on the canvas. */
  passages: readonly Point[]
  /** Its other neighbours that are on the canvas. */
  others: readonly Point[]
  /** How many passages it says it holds (`passage_count` of a recording), else null. */
  passageCount: number | null
}

export interface PlaceOptions {
  /** Where the ring starts: random at every expand, so that two expands do not stack. */
  baseAngle: number
  around: Surroundings
}

/**
 * The ring: `count` places evenly spaced round the anchor from `baseAngle`, never tighter than
 * a sixth of a turn.
 */
export function fanPositions(anchor: Point, count: number, baseAngle: number): Point[] {
  return Array.from({ length: count }, (_, i) => {
    const angle = baseAngle + (i * Math.PI * 2) / Math.max(count, 6)
    return {
      x: anchor.x + Math.cos(angle) * FAN_RADIUS,
      y: anchor.y + Math.sin(angle) * FAN_RADIUS,
    }
  })
}

const mean = (values: readonly number[]) =>
  values.reduce((sum, value) => sum + value, 0) / values.length

/**
 * The side a recording's timeline unrolls on: where its passages already stand, else away from
 * its other neighbours, else the right.
 */
export function timelineSide(
  anchor: Point,
  around: Pick<Surroundings, 'passages' | 'others'>
): TimelineSide {
  if (around.passages.length > 0) {
    return mean(around.passages.map(at => at.x)) < anchor.x ? 'left' : 'right'
  }
  if (around.others.length === 0) return 'right'
  return mean(around.others.map(at => at.x)) > anchor.x ? 'left' : 'right'
}

const isPlace = (value: number | null): value is number =>
  value !== null && Number.isInteger(value) && value >= 0

/**
 * The place of each passage in the block, and how many places the block has. A passage's own
 * number is its place, in a block as large as the recording says it is. Passages that do not
 * all carry a number of their own are placed in the order of the recording instead, side by
 * side with nothing left out.
 */
function timelinePlaces(
  chips: readonly PlacedChild[],
  declared: number | null
): { places: Map<string, number>; total: number } {
  const numbers = chips.map(chip => chip.index)
  if (numbers.every(isPlace) && new Set(numbers).size === numbers.length) {
    const total = Math.max(isPlace(declared) ? declared : 0, Math.max(...numbers) + 1)
    return { places: new Map(chips.map((chip, at) => [chip.id, numbers[at]])), total }
  }
  const time = (chip: PlacedChild) => chip.startMs ?? chip.index ?? Number.POSITIVE_INFINITY
  // Array.prototype.sort is stable: passages with the same start keep the order they came in.
  const ordered = [...chips].sort((a, b) => time(a) - time(b))
  return { places: new Map(ordered.map((chip, at) => [chip.id, at])), total: chips.length }
}

/**
 * The timeline of a recording: its passage chips in columns beside the anchor, centred on its
 * height. One column while it is no taller than `COLUMN_ROWS`; then as many columns of that
 * height as it takes, and once the block would be wider than tall, taller columns too.
 */
function timelinePositions(
  anchor: Point,
  chips: readonly PlacedChild[],
  options: { side: TimelineSide; passageCount: number | null }
): Map<string, Point> {
  if (chips.length === 0) return new Map()
  const { places, total } = timelinePlaces(chips, options.passageCount)
  const chipWidth = Math.max(...chips.map(chip => chip.width))
  const pitchX = chipWidth + COLUMN_GAP
  const pitchY = Math.max(...chips.map(chip => chip.height)) + ROW_GAP
  const tallest = Math.max(COLUMN_ROWS, Math.ceil(Math.sqrt((total * pitchX) / pitchY)))
  const columns = Math.ceil(total / tallest)
  const rows = Math.ceil(total / columns)
  const blockWidth = columns * pitchX - COLUMN_GAP
  // Read from the left on either side: on the left the first column is the far one.
  const nearEdge =
    options.side === 'right'
      ? anchor.x + TIMELINE_CLEARANCE
      : anchor.x - TIMELINE_CLEARANCE - blockWidth
  const firstColumn = nearEdge + chipWidth / 2
  const firstRow = anchor.y - ((rows - 1) / 2) * pitchY
  return new Map(
    chips.map(chip => {
      const place = places.get(chip.id) ?? 0
      const column = Math.floor(place / rows)
      return [chip.id, { x: firstColumn + column * pitchX, y: firstRow + (place % rows) * pitchY }]
    })
  )
}

/**
 * Where each of `children`, which arrive beside the node at `anchor`, is put. Passages go to
 * the recording's timeline; everything else is fanned on the ring, in the order it came, as if
 * the passages were not there.
 */
export function placeAround(
  anchor: Point,
  children: readonly PlacedChild[],
  options: PlaceOptions
): Map<string, Point> {
  const passages = children.filter(child => child.typeLabel === PASSAGE_LABEL)
  const others = children.filter(child => child.typeLabel !== PASSAGE_LABEL)
  const ring = fanPositions(anchor, others.length, options.baseAngle)
  const timeline = timelinePositions(anchor, passages, {
    side: timelineSide(anchor, options.around),
    passageCount: options.around.passageCount,
  })
  return new Map([...others.map((child, at) => [child.id, ring[at]] as const), ...timeline])
}

// ── on the canvas ──────────────────────────────────────────────────────────

const nonNegative = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null

/** A canvas node as the placement reads it. */
function placedChild(node: NodeSingular): PlacedChild {
  const properties: Record<string, unknown> = node.data('properties') ?? {}
  return {
    id: node.id(),
    typeLabel: String(node.data('typeLabel') ?? ''),
    width: Number(node.data('baseWidth')) || 0,
    height: Number(node.data('baseHeight')) || 0,
    index: nonNegative(properties.index),
    startMs: nonNegative(properties.start_ms),
  }
}

/** What stands round `anchor` on the canvas; `arriving` are being placed now and stand nowhere. */
function surroundingsOf(anchor: NodeSingular, arriving: ReadonlySet<string>): Surroundings {
  const passages: Point[] = []
  const others: Point[] = []
  anchor
    .neighborhood()
    .nodes()
    .forEach(node => {
      if (arriving.has(node.id())) return
      const group = node.data('typeLabel') === PASSAGE_LABEL ? passages : others
      group.push({ ...node.position() })
    })
  const properties: Record<string, unknown> = anchor.data('properties') ?? {}
  return { passages, others, passageCount: nonNegative(properties.passage_count) }
}

/**
 * Put `children`, the new nodes that arrive beside `anchor`, on the canvas. `arriving` holds
 * the id of every node being placed in this update.
 */
export function placeBesideAnchor(
  anchor: NodeSingular,
  children: readonly NodeSingular[],
  arriving: ReadonlySet<string>,
  baseAngle: number
): void {
  const holdsPassage = children.some(child => child.data('typeLabel') === PASSAGE_LABEL)
  // Only a timeline looks at what stands round the anchor: the ring never did.
  const around = holdsPassage
    ? surroundingsOf(anchor, arriving)
    : { passages: [], others: [], passageCount: null }
  const spots = placeAround(anchor.position(), children.map(placedChild), { baseAngle, around })
  for (const child of children) {
    const spot = spots.get(child.id())
    if (spot) child.position(spot)
  }
}
